// Đổi danh phận (QĐ 0255) — phần chạy chung cho mọi màn.
//
// Máy chủ nhớ danh phận đang dùng theo (máy, tài khoản) và gắn đầu thư
// X-Gnpf-Persona lên mọi phản hồi của phiên cookie. Màn chỉ làm ba việc:
//   1. đọc khối `persona` trong vé đăng nhập để vẽ menu + dải màu;
//   2. so đầu thư với kho — lệch thì danh phận vừa bị gỡ, hoặc vừa đổi ở thẻ
//      khác ⇒ báo rồi tải lại, KHÔNG để màn vẽ quyền cũ mà máy chủ trả quyền mới;
//   3. đổi xong thì báo các thẻ khác qua localStorage.
import { toast } from "sonner";
import { shouldSendBearer } from "@/lib/api/authed-fetch";
import { useAuthV2, type PersonaOption, type PersonaState } from "./store";

export const PERSONA_HEADER = "X-Gnpf-Persona";
export const PERSONA_PRIMARY = "primary";
export const PERSONA_BROADCAST_KEY = "gnpf:persona-switched";

const RELOAD_DELAY_MS = 1200;

/**
 * Có menu đổi danh phận không. Vé Bearer (không cookie) không có máy để nhớ
 * lựa chọn, máy chủ cũng từ chối đổi — nên giấu hẳn.
 */
export function hasPersonas(state: PersonaState | null | undefined): state is PersonaState {
  return !!state && state.personas.length > 0 && !shouldSendBearer();
}

/** Danh phận chính đứng đầu, rồi tới các danh phận phụ theo thứ tự gán. */
export function personaOptions(state: PersonaState): PersonaOption[] {
  return [state.primary, ...state.personas];
}

/** Danh phận đang dùng; mã không có trong danh sách thì là danh phận chính. */
export function activePersona(state: PersonaState): PersonaOption {
  return state.personas.find((p) => p.id === state.activeId) ?? state.primary;
}

export function isSecondaryActive(state: PersonaState | null | undefined): boolean {
  return !!state && state.activeId !== "" && state.personas.some((p) => p.id === state.activeId);
}

/**
 * Trang mở đầu sau khi đổi: QC vào Bảng QC; thợ chụp vào "Việc thợ chụp" (bản
 * mẫu v2 CEO duyệt: "app mở thẳng trang Việc thợ chụp"); thợ trang điểm — khối
 * không có trang đó — vào "Suất của tôi"; còn lại vào /home — /home tự chọn
 * Lịch chụp hay Tổng quan.
 */
export function personaHomePath(p: Pick<PersonaOption, "role" | "department">): string {
  const role = p.role.toLowerCase();
  const dept = p.department.toLowerCase();
  if (role === "qc") return "/qc-board";
  if (role === "staff" && dept.startsWith("photo")) return "/photo-work";
  if (role === "staff" && dept === "makeup") return "/my-dispatch";
  return "/home";
}

let switching = false;
let reloading = false;

/** Đang tự đổi ở thẻ này ⇒ bỏ qua đầu thư lệch cho tới khi trang tải lại. */
export function markPersonaSwitching(on: boolean): void {
  switching = on;
}

function scheduleReload(message: string, reload: () => void): void {
  if (reloading) return;
  reloading = true;
  toast.info(message);
  window.setTimeout(reload, RELOAD_DELAY_MS);
}

/** So đầu thư X-Gnpf-Persona của một phản hồi với danh phận kho đang vẽ. */
export function notePersonaHeader(
  res: Response,
  reload: () => void = () => window.location.reload(),
): void {
  if (switching) return;
  const header = res.headers.get(PERSONA_HEADER);
  if (!header) return;
  const state = useAuthV2.getState().persona;
  if (!state) return;
  const served = header === PERSONA_PRIMARY ? "" : header;
  if (served === state.activeId) return;
  if (served === "" && isSecondaryActive(state)) {
    const gone = activePersona(state).label;
    scheduleReload(
      `Danh phận ${gone} của bạn vừa được gỡ. App đã tự chuyển về ${state.primary.label}. Các suất bạn đã nhận vẫn giữ nguyên, trưởng phòng sẽ xử lý.`,
      reload,
    );
    return;
  }
  scheduleReload("Danh phận vừa được đổi ở một thẻ khác — đang tải lại trang.", reload);
}

/** Báo các thẻ khác của cùng trình duyệt rằng danh phận vừa đổi. */
export function broadcastPersonaSwitch(): void {
  try {
    window.localStorage.setItem(PERSONA_BROADCAST_KEY, String(Date.now()));
  } catch {
    // Kho trình duyệt bị chặn: thẻ khác vẫn tự phát hiện qua đầu thư ở lời
    // gọi kế tiếp, chỉ chậm hơn.
  }
}

/** Nghe thẻ khác đổi danh phận. Trả hàm gỡ lắng nghe. */
export function listenPersonaSwitch(
  reload: () => void = () => window.location.reload(),
): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key !== PERSONA_BROADCAST_KEY || switching) return;
    scheduleReload("Danh phận vừa được đổi ở một thẻ khác — đang tải lại trang.", reload);
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}

/** Chỉ cho bài kiểm: trả hai cờ về ban đầu. */
export function __resetPersonaSyncForTest(): void {
  switching = false;
  reloading = false;
}
